/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useContext } from 'react';
import { map, isEqual, get } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { Form, FormRenderProps, FormSpy } from 'react-final-form';
import { ActionButton, BrandColors, Header, Mixins } from '@blend/enterprise-components';
import { inputFormatters } from '@blend/shared-react';
import { createThread, updateVersionedFees, createMessage } from 'blb-api/loans';
import { CLOSING_FEES_FORM_CONFIG, SECTION_AGGREGATION_CONFIGS } from '../../constants';
import { FeesContext } from '../FeesTab';
import { aggregateFeesBySubsection, generateFeesPayloadForUpdate } from '../../utils/closingFeesUtils';
import { ClosingFeesLedgerSubsection } from '../../components/ClosingFeesLedgerSubsection';
import { Pricing } from 'blb-types/pricing';
import { ReduxProps } from './ClosingFeesFormContainer';
import { FeeFormValue } from '../../types';
import { DRAWER_BAR_WIDTH, DRAWER_WIDTH, TRANSITION } from '../Drawer/Drawer';

const styles = StyleSheet.create({
  sectionHeaderWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: 16,
  },
  sectionWrapper: {
    backgroundColor: BrandColors.WHITE,
    borderRadius: 3,
    marginBottom: 50,
    padding: 32,
    ...Mixins.border(BrandColors.CLOUD_70, 1, 'solid'),
  },
  submitWrapper: {
    bottom: 32,
    position: 'fixed',
    right: 90,
    transform: 'translateX(0px)',
    transition: TRANSITION,
    zIndex: 1,
  },
  submitWrapperTransition: {
    transform: `translateX(-${DRAWER_WIDTH - DRAWER_BAR_WIDTH}px)`,
  },
  subsectionWrapper: {
    ':not(:last-child)': {
      marginBottom: 40,
    },
  },
});

interface ClosingFeesFormProps {
  drawerOpen: boolean;
  grabLock: () => Promise<void>;
  initialize: (data: Pricing.VersionedFeesResponse) => void;
}

export type OwnProps = ClosingFeesFormProps & ReduxProps;
export const ClosingFeesForm: React.FC<OwnProps> = ({ initialize, getThreadsForLoan, grabLock, drawerOpen }) => {
  const {
    formInitialValues,
    formValues,
    loan,
    order,
    version,
    setErrorType,
    setFormValues,
    actionsEnabled,
  } = useContext(FeesContext);

  const associateMessagesWithFees = async (payload: FeeFormValue[]) => {
    for (const fee of payload) {
      const thread = fee.thread;
      if (!thread) continue;

      if (fee.sharedInboxThreadId) {
        const { user, body, attachments } = thread;
        await createMessage(loan!.loanId, fee.sharedInboxThreadId, { attachments, body, user });
      } else {
        const {
          data: { id: threadId },
        } = await createThread(loan!.loanId, thread);
        fee.sharedInboxThreadId = threadId;
      }
      delete fee.thread;
    }
    return payload;
  };

  const submitFees = async () => {
    const payload = generateFeesPayloadForUpdate(formValues, { version });

    try {
      await grabLock();
      const payloadWithThreadIds = (await associateMessagesWithFees(payload)) as Pricing.VersionedFeeRequest[];
      const response = await updateVersionedFees(loan!.loanId, order!.tenant, payloadWithThreadIds);
      initialize(response.data);
      getThreadsForLoan(loan!.loanId);
    } catch (err) {
      const errType: string = get(err, 'identifier', 'default_error');
      switch (errType) {
        case 'lock':
          setErrorType('LockError');
          break;
        default:
          setErrorType('AcceptFeesError');
      }
    }
  };

  const renderSectionAggregation = (title: string) => {
    let aggregationConfig;
    switch (title) {
      case 'Loan Costs':
        aggregationConfig = SECTION_AGGREGATION_CONFIGS.LoanCosts;
        break;
      case 'Other Costs':
        aggregationConfig = SECTION_AGGREGATION_CONFIGS.OtherCosts;
        break;
      case 'Lender Credits':
        aggregationConfig = SECTION_AGGREGATION_CONFIGS.LenderCredits;
        break;
      default:
        // 'Payoffs and Payments':
        aggregationConfig = SECTION_AGGREGATION_CONFIGS.PayoffsAndPayments;
    }

    const sectionAggregateValue = aggregateFeesBySubsection(formValues, aggregationConfig);

    return (
      <Header fontWeight="BOLD" size={4}>
        {inputFormatters.MONEY_CENTS.formatWithDollarSignAndCents(sectionAggregateValue)}
      </Header>
    );
  };

  return (
    <React.Fragment>
      <Form initialValues={formValues} onSubmit={submitFees}>
        {({ handleSubmit, submitting, active }: FormRenderProps) => {
          return (
            <React.Fragment>
              {active && <FormSpy onChange={state => setFormValues(state.values)} />}
              <div className={css(styles.submitWrapper, drawerOpen && styles.submitWrapperTransition)}>
                <ActionButton
                  data-it="SubmitRequests-Button"
                  disabled={!actionsEnabled || isEqual(formValues, formInitialValues)}
                  isLoading={submitting}
                  label="Submit for approval"
                  onClick={() => handleSubmit()}
                  theme={'primary'}
                />
              </div>
              {map(CLOSING_FEES_FORM_CONFIG, ({ title, subsections }) => (
                <div data-it={`LedgerSection-${title}`} key={title}>
                  <div className={css(styles.sectionHeaderWrapper)} data-it="LedgerSectionHeader">
                    <Header fontWeight="BOLD" size={4}>
                      {title}
                    </Header>
                    {renderSectionAggregation(title)}
                  </div>
                  <div className={css(styles.sectionWrapper)}>
                    {map(subsections, subsectionConfig => (
                      <div
                        className={css(styles.subsectionWrapper)}
                        data-it={`LedgerSubsection-${subsectionConfig.name}`}
                        key={subsectionConfig.name}
                      >
                        <ClosingFeesLedgerSubsection config={subsectionConfig} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </React.Fragment>
          );
        }}
      </Form>
    </React.Fragment>
  );
};
